/**
 * Chat-side export helpers - lighter than features/reporting/export because
 * the chat surface only needs (a) a single-table PDF and (b) a chart canvas
 * snapshot. jspdf is dynamic-imported from callers so the main bundle
 * stays lean; chart canvases already live in the DOM so they're free.
 */
import type { ReportBrand } from '@/lib/pdf'

function safeFilenameStub(title: string): string {
  return title.replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '') || 'download'
}

export async function downloadPdfFromElementTable(
  root: HTMLElement | null,
  title: string,
  brand?: ReportBrand,
): Promise<boolean> {
  if (!root) return false
  const table = root.querySelector('table') as HTMLTableElement | null
  if (!table) return false
  const [{ jsPDF }, autoTableMod, pdfMod] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    import('@/lib/pdf'),
  ])
  const autoTable = (autoTableMod as unknown as { default: typeof import('jspdf-autotable').default }).default
  const { drawBrandHeader, paintBrandedFooters, PDF_COLORS, PDF_FONT, PDF_LAYOUT } = pdfMod
  const doc = new jsPDF({ orientation: 'landscape' })
  let startY = PDF_LAYOUT.topMargin + 6
  if (brand) startY = drawBrandHeader(doc, brand, title)
  // Why: didDrawPage re-stamps the brand header on every page; without a
  // matching margin.top, autoTable would start page 2+ at its default top
  // and the table rows would render underneath the re-stamped header.
  const headerTop = brand ? startY : PDF_LAYOUT.topMargin
  autoTable(doc, {
    html: table,
    startY,
    margin: { top: headerTop, left: PDF_LAYOUT.margin, right: PDF_LAYOUT.margin },
    styles: {
      fontSize: PDF_FONT.body,
      cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
      lineColor: PDF_COLORS.hairline,
      lineWidth: PDF_LAYOUT.thinLine,
      textColor: PDF_COLORS.body,
    },
    headStyles: {
      fillColor: PDF_COLORS.brandPrimary,
      textColor: PDF_COLORS.white,
      fontStyle: 'bold',
      fontSize: PDF_FONT.metaLabel,
    },
    alternateRowStyles: { fillColor: PDF_COLORS.panel },
    didDrawPage: () => {
      if (brand) drawBrandHeader(doc, brand, title)
    },
    theme: 'grid',
  })
  if (brand) paintBrandedFooters(doc, brand, title)
  doc.save(`${safeFilenameStub(title)}.pdf`)
  return true
}

/**
 * Save the first <canvas> inside `root` as a JPEG. Chart.js draws on a
 * transparent canvas so we composite onto a solid white rectangle first -
 * without that the exported JPEG comes out with black bars wherever the
 * chart had transparency. Dimensions match the canvas' pixel resolution.
 */
export function downloadChartAsImage(
  root: HTMLElement | null,
  title: string,
  mime: 'image/jpeg' | 'image/png' = 'image/jpeg',
): boolean {
  if (!root) return false
  const canvas = root.querySelector('canvas') as HTMLCanvasElement | null
  if (!canvas) return false

  const exportCanvas = document.createElement('canvas')
  exportCanvas.width = canvas.width
  exportCanvas.height = canvas.height
  const exportCtx = exportCanvas.getContext('2d')
  if (!exportCtx) return false
  if (mime === 'image/jpeg') {
    exportCtx.fillStyle = '#ffffff'
    exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height)
  }
  exportCtx.drawImage(canvas, 0, 0)
  const dataUrl = exportCanvas.toDataURL(mime, 0.92)
  const ext = mime === 'image/jpeg' ? 'jpg' : 'png'
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = `${safeFilenameStub(title)}.${ext}`
  link.click()
  return true
}
